import { Box, Divider } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TestrunResultVerifyDialog } from './TestrunResultVerifyDialog';
import { selectTestResultsByTestcase, selectTestRun } from '@pages/testruns/store/selectors';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { UI_FUNCTIONAL_TESTING_PATH, UI_TEST_CASE_PATH } from '@shared/constants/pathsConstants';
import UtmsButton from '@shared/components/ui/UtmsButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeployRunModalForTests from '@pages/stands/components/DeployModal/DeployRunModalForTests';
import TestrunResultAssigneeDialog from './TestrunResultAssigneeDialog';
import {
    getTestResultsByTestcaseRequest,
    getTestResultsByTestcaseSuccess,
    getTestResultsRequest,
    getTestRunByIdRequest,
} from '../store';
import { HAND_TEST_RUN_STATUS } from '../constants/testrunsConstants';

const TestrunResultsTableToolbar = ({ selectedTestRunResultsIds, clearSelection, pagination }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const testrun = useSelector(selectTestRun);
    const [openTestcaseRunModal, setOpenTestcaseRunModal] = useState(false);
    const selectedTestRunResultsObjects = useSelector(selectTestResultsByTestcase);
    const { autotests, handtests } = useMemo(() => {
        const auto = [];
        const hand = [];

        selectedTestRunResultsIds.forEach((item) => {
            const [prefix, number] = item.split('-');
            const numericId = Number(number);

            if (prefix === 'ATR') {
                auto.push(numericId);
            }

            if (prefix === 'HTR') {
                hand.push(numericId);
            }
        });

        return {
            autotests: auto,
            handtests: hand,
        };
    }, [selectedTestRunResultsIds]);

    useEffect(() => {
        if (!autotests.length && !handtests.length) {
            dispatch(getTestResultsByTestcaseSuccess({ data: { items: [] } }));
            return;
        }

        dispatch(getTestResultsByTestcaseRequest({ autotests, handtests }));
    }, [dispatch, autotests, handtests]);

    const allSelectedTestResultsAreEqual = useMemo(() => {
        if (!selectedTestRunResultsObjects.length) {
            return;
        }

        return selectedTestRunResultsObjects.every(
            (object, _index) => !object.testcase.is_handtest && object.result === 'failed_na',
        );
    }, [selectedTestRunResultsObjects]);

    const onlyOneTestcaseSelected = selectedTestRunResultsObjects.length === 1;
    const canRerun =
        onlyOneTestcaseSelected &&
        [HAND_TEST_RUN_STATUS.COMPLETED, HAND_TEST_RUN_STATUS.CANCELLED].some(
            (status) => status.toLowerCase() === selectedTestRunResultsObjects[0]?.status?.toLowerCase(),
        );

    const selectedTestcaseExists = onlyOneTestcaseSelected && selectedTestRunResultsObjects[0]?.testcase?.pk;
    const selectedTestcaseObject = useMemo(() => {
        if (!onlyOneTestcaseSelected) return null;
        const testcase = selectedTestRunResultsObjects[0]?.testcase;
        if (!testcase) return null;
        return structuredClone(testcase);
    }, [onlyOneTestcaseSelected, selectedTestRunResultsObjects]);

    const handleOpenDeployRunModal = () => {
        setOpenTestcaseRunModal(true);
    };

    const handleRerunSuccess = useCallback(() => {
        setTimeout(() => {
            dispatch(getTestRunByIdRequest(testrun.pk));
            dispatch(
                getTestResultsRequest({
                    testrun: testrun.pk,
                    latest_only: true,
                    page_index: pagination.pageIndex,
                    page_size: pagination.pageSize,
                }),
            );
        }, 3000);
        clearSelection();
    }, [clearSelection, dispatch, testrun?.pk, pagination]);

    return (
        <>
            <DeployRunModalForTests
                open={openTestcaseRunModal}
                setOpen={setOpenTestcaseRunModal}
                entityType="testcase"
                entity={selectedTestcaseObject}
                params={{ testrunId: testrun.pk }}
                onSuccess={handleRerunSuccess}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TestrunResultVerifyDialog
                    testrun={testrun}
                    testrunId={testrun.pk}
                    verifyResults
                    selectedTestRunResultsObjects={selectedTestRunResultsObjects}
                    disabled={
                        (!onlyOneTestcaseSelected && !allSelectedTestResultsAreEqual) ||
                        (onlyOneTestcaseSelected && !selectedTestRunResultsObjects[0].result)
                    }
                    readonlyMode={!allSelectedTestResultsAreEqual}
                    disableReason={!allSelectedTestResultsAreEqual ? t('group_verification_disabled_warning') : ''}
                />
                <TestrunResultAssigneeDialog
                    testrunId={testrun.pk}
                    selectedTestRunResultsObjects={selectedTestRunResultsObjects}
                    disabled={!selectedTestRunResultsObjects.length}
                />
                {/* <TestrunJiraBugsDialog
                    testrunId={testrun.pk}
                    selectedTestRunResultsObjects={selectedTestRunResultsObjects}
                    disabled={!selectedTestcaseExists}
                /> */}
                <Divider orientation="vertical" flexItem />
                <UtmsButton
                    size="small"
                    disabled={!canRerun}
                    startIcon={<PlayArrowIcon />}
                    onClick={handleOpenDeployRunModal}
                >
                    {t('rerun')}
                </UtmsButton>
                <Divider orientation="vertical" flexItem />
                <UtmsButton
                    disabled={!selectedTestcaseExists}
                    size="small"
                    startIcon={<HistoryOutlinedIcon />}
                    onClick={() => {
                        const testcasePk = selectedTestRunResultsObjects[0].testcase.pk;
                        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testcasePk}/history`);
                    }}
                >
                    {t('history')}
                </UtmsButton>
                <UtmsButton
                    disabled={!selectedTestcaseExists}
                    size="small"
                    startIcon={<ScienceOutlinedIcon />}
                    component="a"
                    href={
                        selectedTestcaseExists
                            ? `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${selectedTestRunResultsObjects[0].testcase.pk}`
                            : undefined
                    }
                    onClick={() => {
                        const testcasePk = selectedTestRunResultsObjects[0].testcase.pk;
                        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testcasePk}`);
                    }}
                >
                    {t('go_to_testcase_definition')}
                </UtmsButton>
            </Box>
        </>
    );
};

export default TestrunResultsTableToolbar;
